import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import HandshakeLogo from './HandshakeLogo';
import { 
  FileText, 
  Receipt, 
  Shield, 
  LayoutDashboard, 
  Menu, 
  X,
  LogOut,
  User,
  HelpCircle,
  MessageCircle,
  Star,
  Database,
  Settings,
  Mail,
  Zap,
  Sun,
  Moon
} from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import ReviewPopup from './ReviewPopup';
import ContactUsDialog from './ContactUsDialog';
import PricingModal from './PricingModal';
import { useTheme } from '../contexts/ThemeContext';

export default function Navbar() {
  const { user, logout, isAuthenticated, planInfo } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showReview, setShowReview] = useState(false);
  const [showContact, setShowContact] = useState(false);
  const [showPricing, setShowPricing] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const navLinks = [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/email-generator', label: 'Email Generator', icon: FileText },
    { path: '/invoice-creator', label: 'Invoice Creator', icon: Receipt },
    { path: '/what-should-i-say', label: 'What Should I Say?', icon: MessageCircle },
    { path: '/protection-hub', label: 'Protection Hub', icon: Shield },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-glass" data-testid="navbar">
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center gap-2 flex-shrink-0"
            data-testid="navbar-logo"
          >
            <div className="w-8 h-8 rounded-md bg-[#FF4500] flex items-center justify-center p-1.5">
              <HandshakeLogo className="w-full h-full" />
            </div>
            <span className="font-clash font-semibold text-white text-xl hidden lg:block">
              SettledInFull
            </span>
          </Link>

          {/* Desktop: Authenticated — full horizontal bar */}
          {isAuthenticated ? (
            <div className="hidden md:flex items-center gap-1 flex-1 justify-end ml-4">
              {/* Nav links */}
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-${link.path.slice(1)}`}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                    isActive(link.path)
                      ? 'text-[#FF4500] bg-white/5'
                      : 'text-white/70 hover:text-white hover:bg-white/5'
                  }`}
                >
                  <link.icon className="w-4 h-4" />
                  <span className="hidden lg:inline">{link.label}</span>
                </Link>
              ))}

              {/* Separator */}
              <div className="w-px h-6 bg-white/10 mx-1 hidden lg:block" />

              {/* Guide */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  localStorage.removeItem('settledinfull_walkthrough_seen');
                  localStorage.setItem('settledinfull_replay_walkthrough', 'true');
                  navigate('/dashboard');
                }}
                className="text-white/40 hover:text-white/70 gap-1.5"
                data-testid="nav-walkthrough-btn"
              >
                <HelpCircle className="w-4 h-4" />
                <span className="hidden lg:inline">Guide</span>
              </Button>

              {/* Credit Balance */}
              {planInfo && (
                <span
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/8 border border-white/10 text-xs font-medium text-white/70"
                  data-testid="nav-credit-badge"
                >
                  <Zap className="w-3 h-3 text-[#FF4500]" />
                  {planInfo.credits ?? 0}
                </span>
              )}

              {/* Top Up Credits */}
              <Button
                size="sm"
                onClick={() => setShowPricing(true)}
                className="relative bg-[#FF4500] hover:bg-[#CC3700] text-white gap-1.5 animate-topup-glow"
                data-testid="nav-topup-btn"
              >
                <Zap className="w-4 h-4" />
                <span className="hidden lg:inline">Top Up</span>
              </Button>

              {/* Theme Toggle */}
              <button
                onClick={toggleTheme}
                className="relative w-8 h-8 rounded-full flex items-center justify-center text-white/50 hover:text-white hover:bg-white/5 transition-colors flex-shrink-0"
                data-testid="theme-toggle-btn"
                title={isDark ? 'Switch to Day Mode' : 'Switch to Night Mode'}
              >
                <Sun className={`w-4 h-4 absolute transition-all duration-300 ${isDark ? 'opacity-0 rotate-90 scale-0' : 'opacity-100 rotate-0 scale-100'}`} />
                <Moon className={`w-4 h-4 absolute transition-all duration-300 ${isDark ? 'opacity-100 rotate-0 scale-100' : 'opacity-0 -rotate-90 scale-0'}`} />
              </button>

              {/* Account Settings Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    className="w-9 h-9 rounded-full p-0 bg-[#FF4500]/20 flex-shrink-0"
                    data-testid="user-menu-trigger"
                  >
                    <Settings className="w-4 h-4 text-[#FF4500]" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 bg-[#121216] border-white/10">
                  <div className="px-3 py-2">
                    <p className="text-sm font-medium text-white">{user?.name}</p>
                    <p className="text-xs text-white/50">{user?.email}</p>
                  </div>
                  <DropdownMenuSeparator className="bg-white/10" />
                  {user?.role === 'admin' && (
                    <DropdownMenuItem 
                      onClick={() => navigate('/admin/users')}
                      className="text-white/70 hover:text-white cursor-pointer"
                      data-testid="admin-users-link"
                    >
                      <Database className="w-4 h-4 mr-2" />
                      User Database
                    </DropdownMenuItem>
                  )}
                  {user?.role === 'admin' && (
                    <DropdownMenuItem 
                      onClick={() => navigate('/admin/reviews')}
                      className="text-white/70 hover:text-white cursor-pointer"
                      data-testid="admin-reviews-link"
                    >
                      <Star className="w-4 h-4 mr-2" />
                      Review Manager
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem 
                    onClick={() => setShowReview(true)}
                    className="text-amber-400 hover:text-amber-300 cursor-pointer"
                    data-testid="leave-review-btn"
                  >
                    <Star className="w-4 h-4 mr-2" />
                    Leave a Review
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => navigate('/account/settings')}
                    className="text-white/70 hover:text-white cursor-pointer"
                    data-testid="account-settings-link"
                  >
                    <User className="w-4 h-4 mr-2" />
                    Account Settings
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => setShowContact(true)}
                    className="text-white/70 hover:text-white cursor-pointer"
                    data-testid="contact-us-link"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Contact Us
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuItem 
                    onClick={handleLogout}
                    className="text-red-400 hover:text-red-300 cursor-pointer"
                    data-testid="logout-btn"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ) : (
            /* Desktop: Public */
            <div className="hidden md:flex items-center gap-3">
              <button
                onClick={toggleTheme}
                className="relative w-8 h-8 rounded-full flex items-center justify-center text-white/50 hover:text-white hover:bg-white/5 transition-colors"
                data-testid="theme-toggle-btn-public"
                title={isDark ? 'Switch to Day Mode' : 'Switch to Night Mode'}
              >
                <Sun className={`w-4 h-4 absolute transition-all duration-300 ${isDark ? 'opacity-0 rotate-90 scale-0' : 'opacity-100 rotate-0 scale-100'}`} />
                <Moon className={`w-4 h-4 absolute transition-all duration-300 ${isDark ? 'opacity-100 rotate-0 scale-100' : 'opacity-0 -rotate-90 scale-0'}`} />
              </button>
              <Link to="/login">
                <Button 
                  variant="ghost" 
                  className="text-white/70 hover:text-white"
                  data-testid="nav-login"
                >
                  Log in
                </Button>
              </Link>
              <Link to="/register">
                <Button 
                  className="bg-[#FF4500] hover:bg-[#CC3700] text-white"
                  data-testid="nav-register"
                >
                  Get Started
                </Button>
              </Link>
            </div>
          )}

          {/* Mobile: Theme Toggle + Menu Button */}
          <div className="md:hidden flex items-center gap-1">
            <button
              onClick={toggleTheme}
              className="relative w-9 h-9 rounded-full flex items-center justify-center text-white/50 hover:text-white transition-colors"
              data-testid="mobile-theme-toggle-top"
              title={isDark ? 'Switch to Day Mode' : 'Switch to Night Mode'}
            >
              <Sun className={`w-5 h-5 absolute transition-all duration-300 ${isDark ? 'opacity-0 rotate-90 scale-0' : 'opacity-100 rotate-0 scale-100'}`} />
              <Moon className={`w-5 h-5 absolute transition-all duration-300 ${isDark ? 'opacity-100 rotate-0 scale-100' : 'opacity-0 -rotate-90 scale-0'}`} />
            </button>
            <button
              className="p-2 text-white"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-white/10">
            <div className="flex flex-col gap-2">
              {isAuthenticated && navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-md ${
                    isActive(link.path)
                      ? 'text-[#FF4500] bg-white/5'
                      : 'text-white/70'
                  }`}
                >
                  <link.icon className="w-5 h-5" />
                  {link.label}
                </Link>
              ))}
              
              <div className="border-t border-white/10 my-2 pt-2">
                {isAuthenticated ? (
                  <>
                    {user?.role === 'admin' && (
                      <Link
                        to="/admin/users"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center gap-3 px-4 py-3 text-white/70"
                        data-testid="mobile-admin-users-link"
                      >
                        <Database className="w-5 h-5" />
                        User Database
                      </Link>
                    )}
                    {user?.role === 'admin' && (
                      <Link
                        to="/admin/reviews"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center gap-3 px-4 py-3 text-white/70"
                        data-testid="mobile-admin-reviews-link"
                      >
                        <Star className="w-5 h-5" />
                        Review Manager
                      </Link>
                    )}
                    <button
                      onClick={() => {
                        setShowPricing(true);
                        setMobileMenuOpen(false);
                      }}
                      className="flex items-center gap-3 px-4 py-3 text-[#FF4500] font-medium w-full"
                      data-testid="mobile-topup-btn"
                    >
                      <Zap className="w-5 h-5" />
                      Top Up Credits
                      {planInfo && (
                        <span className="ml-auto text-xs bg-white/10 text-white/60 px-2 py-0.5 rounded-full" data-testid="mobile-credit-badge">
                          {planInfo.credits ?? 0} credits
                        </span>
                      )}
                    </button>
                    <button
                      onClick={() => {
                        localStorage.removeItem('settledinfull_walkthrough_seen');
                        localStorage.setItem('settledinfull_replay_walkthrough', 'true');
                        setMobileMenuOpen(false);
                        navigate('/dashboard');
                      }}
                      className="flex items-center gap-3 px-4 py-3 text-white/50 w-full"
                      data-testid="mobile-walkthrough-btn"
                    >
                      <HelpCircle className="w-5 h-5" />
                      Walkthrough Guide
                    </button>
                    <button
                      onClick={toggleTheme}
                      className="flex items-center gap-3 px-4 py-3 text-white/50 w-full"
                      data-testid="mobile-theme-toggle"
                    >
                      {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                      {isDark ? 'Day Mode' : 'Night Mode'}
                    </button>
                    <button
                      onClick={() => {
                        setShowReview(true);
                        setMobileMenuOpen(false);
                      }}
                      className="flex items-center gap-3 px-4 py-3 text-amber-400 w-full"
                      data-testid="mobile-leave-review-btn"
                    >
                      <Star className="w-5 h-5" />
                      Leave a Review
                    </button>
                    <Link
                      to="/account/settings"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-3 px-4 py-3 text-white/70"
                      data-testid="mobile-account-settings-link"
                    >
                      <Settings className="w-5 h-5" />
                      Account Settings
                    </Link>
                    <button
                      onClick={() => {
                        setShowContact(true);
                        setMobileMenuOpen(false);
                      }}
                      className="flex items-center gap-3 px-4 py-3 text-white/70 w-full"
                      data-testid="mobile-contact-us-link"
                    >
                      <Mail className="w-5 h-5" />
                      Contact Us
                    </button>
                    <button
                      onClick={() => {
                        handleLogout();
                        setMobileMenuOpen(false);
                      }}
                      className="flex items-center gap-3 px-4 py-3 text-red-400 w-full"
                    >
                      <LogOut className="w-5 h-5" />
                      Log out
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={toggleTheme}
                      className="flex items-center gap-3 px-4 py-3 text-white/50 w-full"
                      data-testid="mobile-theme-toggle"
                    >
                      {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                      {isDark ? 'Day Mode' : 'Night Mode'}
                    </button>
                    <Link
                      to="/login"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-3 px-4 py-3 text-white/70"
                    >
                      Log in
                    </Link>
                    <Link
                      to="/register"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-3 px-4 py-3 text-[#FF4500]"
                    >
                      Get Started
                    </Link>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Review Popup - rendered via portal to escape nav positioning */}
      {showReview && createPortal(
        <ReviewPopup
          planType={null}
          onClose={() => setShowReview(false)}
        />,
        document.body
      )}

      {/* Contact Us Dialog */}
      <ContactUsDialog
        open={showContact}
        onClose={() => setShowContact(false)}
      />

      {/* Pricing Modal */}
      <PricingModal
        open={showPricing}
        onClose={() => setShowPricing(false)}
      />

      {/* Glow animation styles */}
      <style>{`
        @keyframes topup-glow {
          0%, 100% { box-shadow: 0 0 6px rgba(255, 69, 0, 0.3); }
          50% { box-shadow: 0 0 18px rgba(255, 69, 0, 0.55), 0 0 30px rgba(255, 69, 0, 0.2); }
        }
        .animate-topup-glow {
          animation: topup-glow 2.5s ease-in-out infinite;
        }
      `}</style>
    </nav>
  );
}
